/**
 * HerreroScreen — El herrero.
 *
 * Es la mejor idea de Minecraft Dungeons II y resuelve un problema viejo de
 * los juegos de botín: te encariñas con un arma y al rato cae otra mejor, así
 * que tiras la que te gustaba. Aquí no. El herrero coge la que llevas y la
 * sube, así que la espada con la que Fede mató al primer creeper puede seguir
 * siendo su espada hasta el final.
 *
 * Dos servicios, los dos pagados en esmeraldas:
 *
 *   MEJORAR   sube la rareza un nivel y añade un bono nuevo. Conserva los que
 *             ya tenía, que es justo lo que hace que valga la pena.
 *   RE-TIRAR  vuelve a tirar los bonos, misma rareza. Para cuando salieron
 *             tres que no te sirven.
 *
 * El precio sube con lo que estás comprando, no con lo que tienes: subir a
 * legendaria cuesta mucho porque una legendaria vale mucho.
 */
import { RAREZAS, rarezaPorId, tirarObjeto, describir, colorDe, BONOS } from '../items/Rareza.js';
import { ItemId, getItemName } from '../items/ItemRegistry.js';

/** Esmeraldas por servicio. El índice es la rareza de DESTINO. */
const PRECIO_MEJORA = [0, 3, 8, 20, 45];
const PRECIO_RETIRADA = 5;

export class HerreroScreen {
  /**
   * @param {object} inventory  para leer el objeto y cobrar las esmeraldas
   * @param {(msg: string) => void} avisar
   */
  constructor(inventory, avisar = () => {}) {
    this.inventory = inventory;
    this.avisar = avisar;
    this.isOpen = false;
    this._build();
  }

  /** Cuántas esmeraldas hay en el inventario. */
  esmeraldas() {
    let n = 0;
    for (const h of this.inventory.slots) {
      if (h && h.itemId === ItemId.EMERALD_GEM) n += h.count || 0;
    }
    return n;
  }

  /** Cobra esmeraldas. Devuelve false si no llegan, sin tocar nada. */
  cobrar(cuantas) {
    if (this.esmeraldas() < cuantas) return false;
    let falta = cuantas;
    for (const h of this.inventory.slots) {
      if (!h || h.itemId !== ItemId.EMERALD_GEM || falta <= 0) continue;
      const quita = Math.min(h.count, falta);
      h.count -= quita;
      falta -= quita;
    }
    /* Los huecos que quedan a cero se vacían, o la barra enseña «0». */
    for (let i = 0; i < this.inventory.slots.length; i++) {
      const h = this.inventory.slots[i];
      if (h && h.count <= 0) this.inventory.slots[i] = null;
    }
    return true;
  }

  /** El objeto seleccionado en la barra, si admite mejora. */
  objetoActual() {
    const h = this.inventory.slots[this.inventory.selectedIndex];
    return h && h.itemId !== undefined ? h : null;
  }

  /* ── los dos servicios, como funciones puras sobre el hueco ───────────── */

  /** Sube la rareza un nivel y añade un bono, conservando los que tenía. */
  mejorar(hueco) {
    const actual = rarezaPorId(hueco.rareza || 'comun');
    const i = RAREZAS.indexOf(actual);
    if (i >= RAREZAS.length - 1) return { ok: false, razon: 'ya es legendaria' };

    const destino = RAREZAS[i + 1];
    const precio = PRECIO_MEJORA[i + 1];
    if (!this.cobrar(precio)) return { ok: false, razon: `faltan esmeraldas (${precio})` };

    /* El bono nuevo no puede repetir uno que ya tenga: dos veces «Daño» se lee
       como un fallo del juego, no como un premio. */
    const tenidos = new Set((hueco.bonos || []).map((b) => b.id));
    const libres = BONOS.filter((b) => !tenidos.has(b.id));
    hueco.rareza = destino.id;
    hueco.bonos = [...(hueco.bonos || [])];
    if (libres.length) {
      const b = libres[Math.floor(Math.random() * libres.length)];
      hueco.bonos.push({ id: b.id, valor: b.min + Math.floor(Math.random() * (b.max - b.min + 1)) });
    }
    return { ok: true, precio, rareza: destino.id };
  }

  /** Vuelve a tirar los bonos, misma rareza. */
  retirar(hueco) {
    if (!hueco.rareza || hueco.rareza === 'comun') {
      return { ok: false, razon: 'un objeto común no tiene bonos que re-tirar' };
    }
    if (!this.cobrar(PRECIO_RETIRADA)) {
      return { ok: false, razon: `faltan esmeraldas (${PRECIO_RETIRADA})` };
    }
    const nuevo = tirarObjeto({ rareza: hueco.rareza });
    hueco.bonos = nuevo.bonos;
    return { ok: true, precio: PRECIO_RETIRADA };
  }

  /* ── interfaz ─────────────────────────────────────────────────────────── */

  _build() {
    this._overlay = document.createElement('div');
    this._overlay.id = 'herrero';
    this._overlay.style.cssText = `
      position:fixed; inset:0; background:rgba(0,0,0,0.82); z-index:930;
      display:none; justify-content:center; align-items:center;
      font-family:'Courier New',monospace;`;

    const panel = document.createElement('div');
    panel.style.cssText = `
      background:#2A2A2A; border:4px solid; border-color:#4A4A4A #1A1A1A #1A1A1A #4A4A4A;
      padding:22px 26px; width:min(560px,94vw); color:#DDD;
      display:flex; flex-direction:column; gap:14px;`;

    const t = document.createElement('div');
    t.textContent = 'El Herrero';
    t.style.cssText = 'font-size:22px;font-weight:bold;color:#FFD700;text-align:center;';
    panel.appendChild(t);

    const expl = document.createElement('div');
    expl.textContent = 'Mejora el arma que llevas en la mano en vez de tirarla.';
    expl.style.cssText = 'font-size:13px;text-align:center;color:#BBB;';
    panel.appendChild(expl);

    this._ficha = document.createElement('div');
    this._ficha.style.cssText = `
      background:#1E1E1E; border:2px solid #444; padding:14px; min-height:74px;
      display:flex; flex-direction:column; gap:6px;`;
    panel.appendChild(this._ficha);

    const fila = document.createElement('div');
    fila.style.cssText = 'display:flex; gap:10px;';
    this._btnMejorar = this._boton('Mejorar', '#3A7A3A', () => this._hacer('mejorar'));
    this._btnRetirar = this._boton('Re-tirar bonos', '#3A5A8A', () => this._hacer('retirar'));
    fila.appendChild(this._btnMejorar);
    fila.appendChild(this._btnRetirar);
    panel.appendChild(fila);

    this._aviso = document.createElement('div');
    this._aviso.style.cssText = 'font-size:12px;min-height:16px;color:#E8C060;text-align:center;';
    panel.appendChild(this._aviso);

    const cerrar = this._boton('Cerrar', '#555', () => this.close());
    panel.appendChild(cerrar);

    this._overlay.appendChild(panel);
    document.body.appendChild(this._overlay);
  }

  _boton(texto, fondo, alPulsar) {
    const b = document.createElement('button');
    b.textContent = texto;
    b.style.cssText = `
      flex:1; padding:12px; cursor:pointer; font-family:inherit; font-weight:bold;
      background:${fondo}; border:2px solid; border-color:#7A7A7A #1A1A1A #1A1A1A #7A7A7A;
      color:#FFF; font-size:14px;`;
    b.addEventListener('click', alPulsar);
    return b;
  }

  _hacer(que) {
    const h = this.objetoActual();
    if (!h) { this._aviso.textContent = 'Ponte en la mano el objeto que quieres mejorar.'; return; }
    const r = que === 'mejorar' ? this.mejorar(h) : this.retirar(h);
    if (!r.ok) { this._aviso.textContent = `No se puede: ${r.razon}.`; this._pintar(); return; }
    this._aviso.textContent = `Hecho por ${r.precio} esmeraldas.`;
    this.avisar(`${getItemName(h.itemId) || 'Objeto'}: ${describir(h)}`);
    this._pintar();
  }

  _pintar() {
    const h = this.objetoActual();
    this._ficha.textContent = '';
    const linea = (txt, css = '') => {
      const d = document.createElement('div');
      d.textContent = txt;
      d.style.cssText = `font-size:13px;${css}`;
      this._ficha.appendChild(d);
      return d;
    };

    if (!h) {
      linea('No llevas nada en la mano.', 'color:#999;');
      linea('Selecciona un objeto en la barra y vuelve.', 'color:#777;font-size:12px;');
    } else {
      linea(getItemName(h.itemId) || `Objeto ${h.itemId}`,
            `font-size:17px;font-weight:bold;color:${colorDe(h)};`);
      linea(describir(h) || 'Común · sin bonos', 'color:#CCC;');
      const i = RAREZAS.indexOf(rarezaPorId(h.rareza || 'comun'));
      if (i < RAREZAS.length - 1) {
        linea(`Mejorar a ${RAREZAS[i + 1].nombre}: ${PRECIO_MEJORA[i + 1]} esmeraldas`,
              `color:${RAREZAS[i + 1].color};font-size:12px;`);
      } else {
        linea('Ya es legendaria: no se puede subir más.', 'color:#FFB020;font-size:12px;');
      }
      linea(`Re-tirar bonos: ${PRECIO_RETIRADA} esmeraldas`, 'color:#8AB4E8;font-size:12px;');
    }
    linea(`Tienes ${this.esmeraldas()} esmeraldas.`, 'color:#22CC44;font-size:12px;margin-top:4px;');
  }

  open() { this._aviso.textContent = ''; this._pintar(); this._overlay.style.display = 'flex'; this.isOpen = true; }
  close() { this._overlay.style.display = 'none'; this.isOpen = false; }
  toggle() { this.isOpen ? this.close() : this.open(); }
}
